import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import TravelModeBanner from '@/components/trips/TravelModeBanner';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  UsersThree, CurrencyDollar, CalendarBlank, ListChecks,
  CaretRight, Sparkle, CookingPot, Warning, TrendUp,
  Wrench, Car, FolderLock, ShieldWarning,
  GearSix, ArrowsOutCardinal, Eye, EyeSlash, ArrowCounterClockwise, X, DotsSixVertical
} from '@phosphor-icons/react';
import VoiceMicButton from '@/components/VoiceMicButton';
import { PartyCountdown } from '@/components/PartyCountdown';
import FamilyTreeWidget from '@/components/FamilyTreeWidget';
import MagicMomentsWidget from '@/components/MagicMomentsWidget';

const WIDGET_DEFAULTS = [
  { id: 'magic-moments', label: 'Magic Moment', visible: true, colSpan: 12 },
  { id: 'family-tree', label: 'Family', visible: true, colSpan: 12 },
  { id: 'party-countdown', label: 'Party Countdown', visible: true, colSpan: 4 },
  { id: 'ai-briefing', label: 'AI Briefing', visible: true, colSpan: 8 },
  { id: 'todays-menu', label: "Today's Menu", visible: true, colSpan: 4 },
  { id: 'staff-status', label: 'Staff Status', visible: true, colSpan: 4 },
  { id: 'events', label: "Today's Events", visible: true, colSpan: 4 },
  { id: 'pantry', label: 'Pantry Low Stock', visible: true, colSpan: 4 },
  { id: 'expenses', label: 'Recent Expenses', visible: true, colSpan: 6 },
  { id: 'tasks', label: 'Pending Tasks', visible: true, colSpan: 6 },
  { id: 'maintenance-alerts', label: 'Maintenance Alerts', visible: true, colSpan: 4 },
  { id: 'vehicle-alerts', label: 'Vehicle Alerts', visible: true, colSpan: 4 },
  { id: 'document-alerts', label: 'Document Alerts', visible: true, colSpan: 4 },
  { id: 'budget-alerts', label: 'Budget Alerts', visible: true, colSpan: 6 },
];

const COL_SPAN_MAP = {
  4: 'lg:col-span-4',
  6: 'lg:col-span-6',
  8: 'lg:col-span-8',
  12: 'lg:col-span-12',
};

function SortableWidget({ id, children, className = '' }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className={className} {...attributes}>
      <div className="absolute top-3 left-3 z-10 cursor-grab active:cursor-grabbing p-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" {...listeners}>
        <DotsSixVertical size={14} weight="bold" className="text-neutral-500" />
      </div>
      {children}
    </div>
  );
}

export default function DashboardPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState('');
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState(WIDGET_DEFAULTS);
  const [editMode, setEditMode] = useState(false);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [upcomingEvent, setUpcomingEvent] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, layoutRes, eventsRes] = await Promise.all([
        api.get('/dashboard/overview'),
        api.get('/widget-layout'),
        api.get('/events').catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);

      // Find next upcoming active event
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = (eventsRes.data || [])
        .filter(e => e.status === 'active' && e.event_date >= today)
        .sort((a, b) => a.event_date.localeCompare(b.event_date));
      if (upcoming.length > 0) setUpcomingEvent(upcoming[0]);

      if (layoutRes.data?.widgets?.length) {
        const saved = layoutRes.data.widgets;
        const merged = saved.map(sw => {
          const def = WIDGET_DEFAULTS.find(d => d.id === sw.id);
          return { ...def, ...sw, colSpan: def?.colSpan || sw.colSpan || 4 };
        });
        // Inject any new default widgets that didn't exist in the saved layout.
        // New widgets prepended to WIDGET_DEFAULTS are added to the TOP of the
        // user's layout so they're discoverable on first load.
        WIDGET_DEFAULTS.forEach((d, idx) => {
          if (!merged.find(m => m.id === d.id)) {
            if (idx === 0) merged.unshift(d); else merged.push(d);
          }
        });
        setWidgets(merged);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
    setLayoutLoaded(true);
  }, [api]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saveLayout = useCallback(async (newWidgets) => {
    try {
      await api.put('/widget-layout', { widgets: newWidgets });
    } catch (e) { console.error('Failed to save layout', e); }
  }, [api]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const updated = arrayMove(items, oldIndex, newIndex);
        saveLayout(updated);
        return updated;
      });
    }
  };

  const toggleWidgetVisibility = (widgetId) => {
    setWidgets(prev => {
      const updated = prev.map(w => w.id === widgetId ? { ...w, visible: !w.visible } : w);
      saveLayout(updated);
      return updated;
    });
  };

  const resetLayout = async () => {
    try {
      await api.post('/widget-layout/reset');
      setWidgets(WIDGET_DEFAULTS);
    } catch (e) { console.error(e); }
  };

  const loadBriefing = async () => {
    setBriefingLoading(true);
    try {
      const res = await api.post('/ai/chat', { message: 'Give me a morning briefing for today. Include staff attendance, pending tasks, upcoming events, expense status, and any alerts. Be concise.' });
      setBriefing(res.data.response || res.data.message);
    } catch (e) {
      setBriefing('Configure your OpenAI key in Settings > API Keys for AI briefing.');
    }
    setBriefingLoading(false);
  };

  const cs = data?.currency_symbol || 'Rs.';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('good_morning') : hour < 17 ? t('good_afternoon') : t('good_evening');

  const isVisible = (id) => widgets.find(w => w.id === id)?.visible !== false;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const visibleWidgets = widgets.filter(w => w.visible);

  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case 'magic-moments':
        return <MagicMomentsWidget />;
      case 'family-tree':
        return <FamilyTreeWidget />;
      case 'party-countdown':
        if (!upcomingEvent) return (
          <div className="glass-card p-6 flex items-center justify-center min-h-[160px]" data-testid="party-countdown-empty">
            <p className="text-neutral-500 text-xs text-center">No upcoming events.<br />Create one in Events page!</p>
          </div>
        );
        return (
          <div className="glass-card p-6" data-testid="party-countdown-widget">
            <PartyCountdown
              title={upcomingEvent.title}
              date={upcomingEvent.event_date}
              emoji={upcomingEvent.theme_emoji}
              color={upcomingEvent.theme_color || '#FF6B35'}
              venue={upcomingEvent.venue}
              time={upcomingEvent.event_time}
            />
          </div>
        );

      case 'ai-briefing':
        return (
          <div className="glass-card p-6 md:p-8 relative overflow-hidden" data-testid="ai-briefing-card">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#00E5FF] flex items-center justify-center">
                  <Sparkle size={20} weight="fill" className="text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-white text-base">AI Briefing</h3>
                  <p className="text-[11px] text-neutral-500">Your daily household summary</p>
                </div>
              </div>
              {!briefing && (
                <button onClick={loadBriefing} disabled={briefingLoading} data-testid="load-briefing-btn"
                  className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow disabled:opacity-50 flex items-center gap-2">
                  {briefingLoading ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> : <Sparkle size={14} />}
                  {briefingLoading ? 'Generating...' : 'Generate'}
                </button>
              )}
            </div>
            {briefing ? (
              <div className="text-sm text-neutral-300 leading-relaxed whitespace-pre-line">{briefing}</div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-neutral-500 text-sm">Tap "Generate" for your AI-powered morning briefing</p>
              </div>
            )}
          </div>
        );

      case 'todays-menu':
        return (
          <div className="glass-card p-6" data-testid="todays-menu-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CookingPot size={18} weight="duotone" className="text-[#FF6B35]" />
                <span className="overline">{t('todays_menu')}</span>
              </div>
              <button onClick={() => navigate('/meals')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                {t('meal_planner')} <CaretRight size={10} />
              </button>
            </div>
            {data?.meals?.today ? (
              <div className="space-y-3">
                {[{ key: 'breakfast', label: t('breakfast') }, { key: 'lunch', label: t('lunch') }, { key: 'dinner', label: t('dinner') }, { key: 'snacks', label: t('snacks') }]
                  .filter(m => data.meals.today[m.key])
                  .map(m => (
                    <div key={m.key} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] mt-2 flex-shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{m.label}</p>
                        <p className="text-sm text-neutral-200">{data.meals.today[m.key]}</p>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 py-6 text-center">{t('no_meals_planned')}</p>
            )}
          </div>
        );

      case 'staff-status':
        return (
          <div className="glass-card p-6" data-testid="staff-status-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <UsersThree size={18} weight="duotone" className="text-[#22C55E]" />
                <span className="overline">{t('staff_today')}</span>
              </div>
              <button onClick={() => navigate('/staff')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                Manage <CaretRight size={10} />
              </button>
            </div>
            <div className="space-y-2.5">
              {data?.staff?.members?.length > 0 ? data.staff.members.map((s, i) => {
                const st = s.status || (s.checked_in ? 'in' : 'not_arrived');
                const meta = {
                  in: { dot: 'bg-[#22C55E] shadow-[0_0_8px_rgba(34,197,94,0.5)]', chip: 'bg-[#22C55E]/10 text-[#22C55E]', label: `In ${s.check_in ? new Date(s.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}` },
                  late: { dot: 'bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.5)]', chip: 'bg-orange-500/10 text-orange-500', label: `Late ${s.late_minutes}m` },
                  out: { dot: 'bg-neutral-400', chip: 'bg-neutral-500/10 text-neutral-400', label: 'Left' },
                  leave: { dot: 'bg-blue-500', chip: 'bg-blue-500/10 text-blue-500', label: 'On Leave' },
                  holiday: { dot: 'bg-purple-500', chip: 'bg-purple-500/10 text-purple-400', label: 'Holiday' },
                  half_day: { dot: 'bg-amber-500', chip: 'bg-amber-500/10 text-amber-500', label: 'Half Day' },
                  absent: { dot: 'bg-red-500', chip: 'bg-red-500/10 text-red-500', label: t('absent') },
                  not_arrived: { dot: 'bg-neutral-600', chip: 'bg-neutral-800 text-neutral-500', label: s.expected_arrival ? `Due ${s.expected_arrival}` : 'Not arrived' },
                }[st] || { dot: 'bg-neutral-600', chip: 'bg-neutral-800 text-neutral-500', label: st };
                return (
                  <div key={i} className="flex items-center justify-between py-1.5" data-testid={`staff-status-row-${s.staff_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${meta.dot}`} />
                      <div>
                        <p className="text-sm text-white font-medium">{s.name}</p>
                        <p className="text-[11px] text-neutral-500">{s.role}</p>
                      </div>
                    </div>
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${meta.chip}`}>
                      {meta.label}
                    </span>
                  </div>
                );
              }) : (
                <p className="text-sm text-neutral-500 py-4 text-center">{t('no_staff')}</p>
              )}
            </div>
          </div>
        );

      case 'events':
        return (
          <div className="glass-card p-6" data-testid="events-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarBlank size={18} weight="duotone" className="text-[#3B82F6]" />
                <span className="overline">{t('today')}</span>
              </div>
              <button onClick={() => navigate('/calendar')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                {t('calendar')} <CaretRight size={10} />
              </button>
            </div>
            <div className="space-y-2.5">
              {data?.events?.today?.length > 0 ? data.events.today.map((e, i) => (
                <div key={i} className="flex items-start gap-3 py-1">
                  <div className="w-1 h-8 rounded-full mt-0.5" style={{ backgroundColor: e.color || '#FF6B35' }} />
                  <div>
                    <p className="text-sm text-white font-medium">{e.title}</p>
                    <p className="text-[11px] text-neutral-500">{e.start_time || 'All day'}</p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-neutral-500 py-4 text-center">{t('no_events')}</p>
              )}
            </div>
          </div>
        );

      case 'pantry':
        return (
          <div className="glass-card p-6" data-testid="pantry-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Warning size={18} weight="duotone" className={data?.grocery?.low_stock_count > 0 ? 'text-[#F59E0B]' : 'text-neutral-500'} />
                <span className="overline">{t('pantry')}</span>
              </div>
              <button onClick={() => navigate('/grocery')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                Manage <CaretRight size={10} />
              </button>
            </div>
            {data?.grocery?.low_stock_count > 0 ? (
              <div>
                <p className="text-sm font-medium text-[#F59E0B] mb-3">{data.grocery.low_stock_count} {t('low_stock').toLowerCase()}</p>
                <div className="space-y-2">
                  {data.grocery.low_stock_items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-neutral-300">{item.name}</span>
                      <span className="text-xs text-neutral-500">{item.quantity} {item.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-neutral-500 py-4 text-center">{t('pantry_stocked')}</p>
            )}
          </div>
        );

      case 'expenses':
        return (
          <div className="glass-card p-6" data-testid="expenses-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CurrencyDollar size={18} weight="duotone" className="text-[#FF6B35]" />
                <span className="overline">{t('recent_expenses')}</span>
              </div>
              <button onClick={() => navigate('/expenses')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                {t('analytics')} <CaretRight size={10} />
              </button>
            </div>
            <div className="space-y-2.5">
              {data?.expenses?.recent?.length > 0 ? data.expenses.recent.map((e, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <div>
                    <p className="text-sm text-white font-medium">{e.description || e.category}</p>
                    <p className="text-[11px] text-neutral-500">{e.date}</p>
                  </div>
                  <span className="text-sm font-semibold text-[#FF6B35]">{cs}{e.amount?.toLocaleString('en-IN')}</span>
                </div>
              )) : (
                <p className="text-sm text-neutral-500 py-4 text-center">{t('no_expenses')}</p>
              )}
            </div>
          </div>
        );

      case 'tasks':
        return (
          <div className="glass-card p-6" data-testid="tasks-card">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks size={18} weight="duotone" className="text-[#F59E0B]" />
              <span className="overline">{t('pending_tasks')}</span>
            </div>
            <div className="space-y-2.5">
              {data?.tasks?.pending?.length > 0 ? data.tasks.pending.slice(0, 5).map((task, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <p className="text-sm text-neutral-300">{task.title}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold
                    ${task.priority === 'high' ? 'bg-red-500/10 text-red-400' : task.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-neutral-800 text-neutral-500'}`}>
                    {task.priority}
                  </span>
                </div>
              )) : (
                <p className="text-sm text-neutral-500 py-4 text-center">No pending tasks</p>
              )}
            </div>
          </div>
        );

      case 'maintenance-alerts':
        if (!data?.maintenance_alerts?.overdue_count && !data?.maintenance_alerts?.due_soon_count) return null;
        return (
          <div className="glass-card p-6 border-l-2 border-amber-500/30" data-testid="maintenance-alerts-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wrench size={18} weight="duotone" className="text-amber-400" />
                <span className="overline">{t('maintenance')}</span>
              </div>
              <button onClick={() => navigate('/maintenance')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                View <CaretRight size={10} />
              </button>
            </div>
            {data.maintenance_alerts.overdue_count > 0 && (
              <p className="text-sm font-medium text-red-400 mb-2">{data.maintenance_alerts.overdue_count} {t('overdue').toLowerCase()}</p>
            )}
            {data.maintenance_alerts.due_soon_count > 0 && (
              <p className="text-sm font-medium text-amber-400 mb-2">{data.maintenance_alerts.due_soon_count} {t('due_soon').toLowerCase()}</p>
            )}
            <div className="space-y-2 mt-3">
              {[...data.maintenance_alerts.overdue_items, ...data.maintenance_alerts.due_soon_items].slice(0, 4).map((m, i) => (
                <div key={i} className="flex items-center justify-between py-1">
                  <p className="text-sm text-neutral-300">{m.title}</p>
                  <span className="text-[10px] text-neutral-500">{m.next_due}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'vehicle-alerts':
        if (!data?.vehicle_alerts?.count) return null;
        return (
          <div className="glass-card p-6 border-l-2 border-red-500/30" data-testid="vehicle-alerts-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Car size={18} weight="duotone" className="text-red-400" />
                <span className="overline">{t('vehicles')}</span>
              </div>
              <button onClick={() => navigate('/vehicles')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                Manage <CaretRight size={10} />
              </button>
            </div>
            <p className="text-sm font-medium text-red-400 mb-3">{data.vehicle_alerts.count} vehicle{data.vehicle_alerts.count > 1 ? 's' : ''} need attention</p>
            <div className="space-y-2.5">
              {data.vehicle_alerts.items.map((v, i) => (
                <div key={i}>
                  <p className="text-sm text-white font-medium">{v.name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {v.alerts.map((a, j) => (
                      <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">{a}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'document-alerts':
        if (!data?.document_alerts?.expired_count && !data?.document_alerts?.expiring_count) return null;
        return (
          <div className="glass-card p-6 border-l-2 border-[#FF6B35]/30" data-testid="document-alerts-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FolderLock size={18} weight="duotone" className="text-[#FF6B35]" />
                <span className="overline">{t('documents')}</span>
              </div>
              <button onClick={() => navigate('/documents')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                View <CaretRight size={10} />
              </button>
            </div>
            {data.document_alerts.expired_count > 0 && (
              <p className="text-sm font-medium text-red-400 mb-1">{data.document_alerts.expired_count} {t('expired').toLowerCase()}</p>
            )}
            {data.document_alerts.expiring_count > 0 && (
              <p className="text-sm font-medium text-amber-400 mb-1">{data.document_alerts.expiring_count} expiring soon</p>
            )}
            <div className="space-y-2 mt-3">
              {data.document_alerts.items.slice(0, 4).map((d, i) => (
                <div key={i} className="flex items-center justify-between py-1">
                  <p className="text-sm text-neutral-300">{d.name}</p>
                  <span className="text-[10px] text-neutral-500">{d.expiry_date}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'budget-alerts':
        if (!data?.budget_alerts?.length) return null;
        return (
          <div className="glass-card p-6 border-l-2 border-[#FF6B35]/40" data-testid="budget-alerts-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldWarning size={18} weight="duotone" className="text-[#FF6B35]" />
                <span className="overline">{t('budget_alerts')}</span>
              </div>
              <button onClick={() => navigate('/expenses')} className="text-[11px] text-neutral-500 hover:text-[#FF6B35] font-medium flex items-center gap-1 transition-colors duration-200">
                {t('analytics')} <CaretRight size={10} />
              </button>
            </div>
            <div className="space-y-3">
              {data.budget_alerts.map((b, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white font-medium">{b.category}</p>
                    <p className="text-[11px] text-neutral-500">{cs}{b.spent?.toLocaleString('en-IN')} / {cs}{b.limit?.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className={`h-full rounded-full ${b.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`}
                        style={{ width: `${Math.min(b.percentage, 100)}%` }} />
                    </div>
                    <span className={`text-xs font-semibold ${b.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>{b.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div data-testid="dashboard-page" className="space-y-5 sm:space-y-8 relative">
      {/* Travel-mode banner — only shows when a trip has travel_mode_active */}
      <TravelModeBanner context="dashboard" />
      {data?.kids_pending > 0 && (
        <a href="/kids" data-testid="kids-pending-banner"
          className="relative z-10 flex items-center gap-3 glass-card px-4 py-3 border-purple-500/40 hover:border-purple-500/70 transition-colors">
          <span className="text-2xl">🧒</span>
          <p className="text-sm font-semibold text-white flex-1">
            {data.kids_pending} kid approval{data.kids_pending > 1 ? 's' : ''} waiting — tasks done or reward requests
          </p>
          <span className="text-xs font-bold bg-purple-500 text-white px-2.5 py-1 rounded-full">Review →</span>
        </a>
      )}
      {/* Ambient glow orbs */}
      <div className="glow-orb-orange -top-20 -left-20 z-0 hidden sm:block" />
      <div className="glow-orb-teal top-60 right-0 z-0 hidden sm:block" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-neutral-500 text-xs sm:text-sm font-medium">{greeting}, {user?.name || 'Admin'}</p>
          <h1 className="font-heading text-xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">{t('dashboard')}</h1>
          <p className="text-neutral-500 text-xs sm:text-sm mt-1 hidden sm:block">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <VoiceMicButton api={api} onResponse={(r) => { if (r.action === 'create_task' || r.action === 'set_announcement') fetchAll(); }} />
          <button
            onClick={() => setEditMode(!editMode)}
            data-testid="toggle-edit-mode-btn"
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
              editMode
                ? 'bg-[#FF6B35] text-white btn-glow'
                : 'border border-white/10 text-neutral-400 hover:text-white hover:border-white/20'
            }`}
          >
            {editMode ? <X size={14} /> : <GearSix size={14} />}
            {editMode ? 'Done' : 'Customize'}
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 relative z-10">
        <StatCard label={t('staff_today')} value={`${data?.staff?.present || 0}/${data?.staff?.total || 0}`} sub={data?.staff?.late > 0 ? `${data.staff.late} late` : t('present')} color="#22C55E" delay={0} onClick={() => navigate('/staff')} />
        <StatCard label={t('total_spent')} value={`${cs}${(data?.expenses?.month_total || 0).toLocaleString('en-IN')}`} sub={`${data?.expenses?.budget_percentage || 0}% ${t('budget_used').toLowerCase()}`} color="#FF6B35" delay={50} onClick={() => navigate('/expenses')} />
        <StatCard label={t('visitors')} value={data?.visitors?.today_total || 0} sub={`${data?.visitors?.currently_inside || 0} ${t('currently_inside').toLowerCase()}`} color="#3B82F6" delay={100} onClick={() => navigate('/visitors')} />
        <StatCard label={t('pending_tasks')} value={data?.tasks?.pending_count || 0} sub={t('today')} color="#F59E0B" delay={150} onClick={() => navigate('/staff')} />
      </div>

      {/* Widget Configuration Panel */}
      {editMode && (
        <div className="glass-card p-5 relative z-10 animate-fade-up" data-testid="widget-config-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ArrowsOutCardinal size={18} weight="duotone" className="text-[#FF6B35]" />
              <span className="font-heading font-semibold text-white text-sm">Widget Layout</span>
            </div>
            <button onClick={resetLayout} data-testid="reset-layout-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-neutral-400 hover:text-white border border-white/10 hover:border-white/20 transition-colors">
              <ArrowCounterClockwise size={12} /> Reset
            </button>
          </div>
          <p className="text-xs text-neutral-500 mb-4">Toggle widgets on/off. Drag widgets in the grid below to reorder them.</p>
          <div className="flex flex-wrap gap-2">
            {widgets.map(w => (
              <button key={w.id} onClick={() => toggleWidgetVisibility(w.id)} data-testid={`toggle-widget-${w.id}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-200 border ${
                  w.visible
                    ? 'border-[#FF6B35]/30 bg-[#FF6B35]/10 text-[#FF6B35]'
                    : 'border-white/5 bg-white/3 text-neutral-500'
                }`}>
                {w.visible ? <Eye size={12} /> : <EyeSlash size={12} />}
                {w.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bento Grid with Drag & Drop */}
      {editMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 relative z-10">
              {visibleWidgets.map(w => {
                const content = renderWidget(w.id);
                if (!content) return null;
                return (
                  <SortableWidget key={w.id} id={w.id} className={COL_SPAN_MAP[w.colSpan] || 'lg:col-span-4'}>
                    {content}
                  </SortableWidget>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 relative z-10">
          {visibleWidgets.map((w, idx) => {
            const content = renderWidget(w.id);
            if (!content) return null;
            return (
              <div key={w.id} className={`animate-fade-up ${COL_SPAN_MAP[w.colSpan] || 'lg:col-span-4'}`} style={{ animationDelay: `${200 + idx * 50}ms` }}>
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color, delay = 0, onClick }) {
  return (
    <button onClick={onClick} data-testid={`stat-${label?.toLowerCase().replace(/\s/g, '-')}`}
      className="glass-card p-5 text-left animate-fade-up group cursor-pointer" style={{ animationDelay: `${delay}ms` }}>
      <p className="overline mb-2">{label}</p>
      <p className="text-2xl md:text-3xl font-heading font-bold text-white tracking-tight">{value}</p>
      {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
      <div className="absolute top-4 right-4 w-2 h-2 rounded-full opacity-60" style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}60` }} />
    </button>
  );
}
